import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { PageTitle } from "../../components/Layout";
import { Modal } from "../../components/ui-modal";
import { fmtDateTime } from "../../lib/helpers";
import { toast } from "sonner";
import * as Icons from "lucide-react";

const STATUS_STYLE = {
  "envoyé": "bg-bleu-50 text-bleu border-bleu/30",
  "délivré": "bg-bleu-50 text-bleu border-bleu/30",
  "simulé": "bg-ambre-50 text-ambre-texte border-ambre/30",
  "échoué": "bg-rouge-50 text-rouge border-rouge/30",
  "rejeté": "bg-rouge-50 text-rouge border-rouge/30",
};

const pad = (n) => String(n).padStart(2, "0");
const todayISO = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const GRAN_LABEL = { day: "le jour", month: "le mois", year: "l'année" };
const hasBody = (l) => l.body !== null && l.body !== undefined && l.body !== "";

const StatCard = ({ label, value, tone }) => {
  const toneCls = {
    neutral: "text-marine",
    green: "text-bleu",
    amber: "text-ambre-texte",
    red: "text-rouge",
  }[tone || "neutral"];
  return (
    <div data-testid={`email-stat-${label}`} className="bg-sable rounded-sm border border-marine/10 px-4 py-3">
      <div className={`text-2xl font-display ${toneCls}`}>{value}</div>
      <div className="text-xs uppercase tracking-wider text-marine/80 mt-0.5">{label}</div>
    </div>
  );
};

export default function AdminEmailLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [dateGran, setDateGran] = useState("all"); // all | day | month | year
  const [dateVal, setDateVal] = useState("");
  const [resending, setResending] = useState(null);
  const [bulk, setBulk] = useState(null); // { mode: 'all' | 'failed', ids: [] }
  const [bulkBusy, setBulkBusy] = useState(false);
  const [digest, setDigest] = useState({ hour: 8, timezone: "Asia/Dubai" });
  const [digestOpen, setDigestOpen] = useState(false);
  const [digestSaving, setDigestSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/email-logs").then((r) => setLogs(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); api.get("/admin/digest-settings").then((r) => setDigest(r.data)).catch(() => {}); }, []);

  const saveDigest = () => {
    setDigestSaving(true);
    api.put("/admin/digest-settings", digest)
      .then((r) => { setDigest(r.data); toast.success("Réglages du digest enregistrés"); setDigestOpen(false); })
      .catch(() => toast.error("Échec de l'enregistrement"))
      .finally(() => setDigestSaving(false));
  };
  const TIMEZONES = ["Asia/Dubai", "Europe/Paris", "Europe/London", "America/New_York", "UTC"];

  const setGranularity = (g) => {
    setDateGran(g);
    const t = todayISO();
    setDateVal(g === "day" ? t : g === "month" ? t.slice(0, 7) : g === "year" ? t.slice(0, 4) : "");
  };

  const resend = (l) => {
    setResending(l.id);
    api.post(`/admin/email-logs/${l.id}/resend`)
      .then(() => { toast.success(`E-mail renvoyé à ${l.to_email}`); load(); })
      .catch((e) => toast.error(e.response?.data?.detail || "Échec du renvoi"))
      .finally(() => setResending(null));
  };

  const dateMatch = useCallback((l) => dateGran === "all" || !dateVal || (l.created_at || "").startsWith(dateVal), [dateGran, dateVal]);
  const termMatch = useCallback((l, term) => !term || [l.to_email, l.subject, l.context_label].some((v) => (v || "").toLowerCase().includes(term)), []);

  // Périmètre "période + recherche" (indépendant du filtre de statut) — sert au récapitulatif
  const scoped = useMemo(() => {
    const term = q.trim().toLowerCase();
    return logs.filter((l) => dateMatch(l) && termMatch(l, term));
  }, [logs, q, dateMatch, termMatch]);

  const scopedCounts = useMemo(() => {
    const c = { all: scoped.length };
    scoped.forEach((l) => { c[l.status] = (c[l.status] || 0) + 1; });
    return c;
  }, [scoped]);

  const scopedFailedResendable = useMemo(() => scoped.filter((l) => l.status === "échoué" && hasBody(l)), [scoped]);

  // Liste affichée (avec filtre de statut)
  const filtered = useMemo(() => scoped.filter((l) => status === "all" || l.status === status), [scoped, status]);

  const globalCounts = useMemo(() => {
    const c = { all: logs.length };
    logs.forEach((l) => { c[l.status] = (c[l.status] || 0) + 1; });
    return c;
  }, [logs]);

  const dateActive = dateGran !== "all" && !!dateVal;
  const filteredResendable = useMemo(() => filtered.filter(hasBody), [filtered]);
  const bulkTargets = bulk ? (bulk.mode === "failed" ? scopedFailedResendable : filteredResendable) : [];

  const doBulk = () => {
    setBulkBusy(true);
    api.post("/admin/email-logs/resend-bulk", { ids: bulkTargets.map((l) => l.id) })
      .then((r) => {
        const { sent = 0, skipped = 0 } = r.data || {};
        toast.success(`${sent} e-mail(s) renvoyé(s)${skipped ? ` · ${skipped} ignoré(s)` : ""}`);
        setBulk(null); load();
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Échec du renvoi groupé"))
      .finally(() => setBulkBusy(false));
  };

  const periodLabel = dateActive ? `${GRAN_LABEL[dateGran]} ${dateVal}` : "toutes les dates";
  const chips = [
    { key: "all", label: "Tous" },
    { key: "envoyé", label: "Envoyés" },
    { key: "simulé", label: "Simulés" },
    { key: "échoué", label: "Échoués" },
  ];

  return (
    <div>
      <PageTitle overline="Automatisations" title="Journal des e-mails"
        action={
          <div className="flex items-center gap-2">
            <button onClick={() => setBulk({ mode: "all" })} disabled={!dateActive || filteredResendable.length === 0}
              data-testid="email-log-bulk-resend"
              title={dateActive ? "Renvoyer les e-mails de la sélection actuelle (filtre de statut + période)" : "Sélectionnez une période (jour, mois ou année) pour activer le renvoi groupé"}
              className="bg-bordeaux text-white rounded-sm px-4 py-2.5 text-sm hover:bg-bordeaux-hover transition-colors inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
              <Icons.SendHorizontal size={16} /> Renvoyer la sélection{dateActive ? ` (${filteredResendable.length})` : ""}
            </button>
            <button onClick={load} data-testid="email-log-refresh" className="border border-marine/30 text-marine rounded-sm px-4 py-2.5 text-sm hover:bg-marine hover:text-white transition-colors inline-flex items-center gap-2"><Icons.RotateCw size={16} /> Actualiser</button>
          </div>
        } />

      {/* Récapitulatif de la période */}
      <div className="mb-6" data-testid="email-log-summary">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm text-marine/80">Aperçu pour <strong className="text-marine">{periodLabel}</strong>{q.trim() ? ` · recherche « ${q.trim()} »` : ""}</p>
          <div className="flex items-center gap-2">
            <button onClick={() => setDigestOpen((o) => !o)} data-testid="digest-settings-toggle"
              className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-sm border border-marine/20 text-marine hover:bg-creme transition-colors">
              <Icons.Clock size={16} /> Digest quotidien : {String(digest.hour).padStart(2, "0")}h00
            </button>
            {scopedFailedResendable.length > 0 && (
              <button onClick={() => setBulk({ mode: "failed" })} data-testid="email-log-resend-failed"
                className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-sm bg-rouge text-white hover:bg-rouge-hover transition-colors">
                <Icons.RefreshCw size={16} /> Renvoyer les échecs ({scopedFailedResendable.length})
              </button>
            )}
          </div>
        </div>
        {digestOpen && (
          <div className="bg-sable rounded-sm border border-marine/10 p-4 mb-3 flex flex-wrap items-end gap-4" data-testid="digest-settings-panel">
            <div>
              <label className="text-xs uppercase tracking-wider text-marine/80 block mb-1">Heure d'envoi</label>
              <select value={digest.hour} onChange={(e) => setDigest({ ...digest, hour: parseInt(e.target.value, 10) })} data-testid="digest-hour"
                className="text-sm rounded-sm border border-marine/15 bg-white px-2 py-2 focus:outline-none focus:border-marine/40">
                {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{String(i).padStart(2, "0")}h00</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-marine/80 block mb-1">Fuseau horaire</label>
              <select value={digest.timezone} onChange={(e) => setDigest({ ...digest, timezone: e.target.value })} data-testid="digest-tz"
                className="text-sm rounded-sm border border-marine/15 bg-white px-2 py-2 focus:outline-none focus:border-marine/40">
                {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </div>
            <button onClick={saveDigest} disabled={digestSaving} data-testid="digest-save"
              className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-sm bg-marine text-white hover:bg-marine-hover transition-colors disabled:opacity-50">
              {digestSaving ? <Icons.Loader2 size={16} className="animate-spin" /> : <Icons.Check size={16} />} Enregistrer
            </button>
            <p className="text-xs text-marine/80 flex-1 min-w-[200px]">Le récapitulatif des e-mails en échec de la veille est envoyé à l'agence chaque jour à l'heure choisie. Modèle éditable dans « E-mails ».</p>
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Total période" value={scopedCounts.all} tone="neutral" />
          <StatCard label="Envoyés" value={scopedCounts["envoyé"] || 0} tone="green" />
          <StatCard label="Simulés" value={scopedCounts["simulé"] || 0} tone="amber" />
          <StatCard label="Échoués" value={scopedCounts["échoué"] || 0} tone="red" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex flex-wrap gap-2" data-testid="email-log-status-filters">
          {chips.map((c) => (
            <button key={c.key} onClick={() => setStatus(c.key)} data-testid={`log-filter-${c.key}`}
              className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${status === c.key ? "bg-bordeaux text-white border-bordeaux" : "bg-canvas text-marine border-marine/20 hover:border-marine/50"}`}>
              {c.label} ({c.key === "all" ? globalCounts.all : globalCounts[c.key] || 0})
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2" data-testid="email-log-date-filter">
          <Icons.CalendarDays size={16} className="text-marine" />
          <select aria-label="Regrouper les dates" value={dateGran} onChange={(e) => setGranularity(e.target.value)} data-testid="email-log-date-gran"
            className="text-sm rounded-sm border border-marine/15 bg-white px-2 py-2 focus:outline-none focus:border-marine/40">
            <option value="all">Toutes les dates</option>
            <option value="day">Jour</option>
            <option value="month">Mois</option>
            <option value="year">Année</option>
          </select>
          {dateGran === "day" && (
            <input type="date" value={dateVal} onChange={(e) => setDateVal(e.target.value)} data-testid="email-log-date-value"
              className="text-sm rounded-sm border border-marine/15 bg-white px-2 py-2 focus:outline-none focus:border-marine/40" />
          )}
          {dateGran === "month" && (
            <input type="month" value={dateVal} onChange={(e) => setDateVal(e.target.value)} data-testid="email-log-date-value"
              className="text-sm rounded-sm border border-marine/15 bg-white px-2 py-2 focus:outline-none focus:border-marine/40" />
          )}
          {dateGran === "year" && (
            <input type="number" min="2020" max="2100" value={dateVal} onChange={(e) => setDateVal(e.target.value)} data-testid="email-log-date-value"
              className="w-24 text-sm rounded-sm border border-marine/15 bg-white px-2 py-2 focus:outline-none focus:border-marine/40" />
          )}
        </div>

        <div className="relative flex-1 min-w-[200px] max-w-xs ml-auto">
          <Icons.Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-marine" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="email-log-search"
            placeholder="Rechercher un destinataire, un objet…"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-sm border border-marine/15 bg-white focus:outline-none focus:border-marine/40" />
        </div>
      </div>

      <div className="bg-sable rounded-sm border border-marine/10 overflow-hidden" data-testid="email-log-list">
        <div className="hidden md:grid grid-cols-[1.8fr_2.3fr_1.3fr_0.9fr_108px] gap-4 px-5 py-3 text-xs uppercase tracking-wider text-marine/80 border-b border-marine/10 bg-sable">
          <span>Destinataire</span><span>Objet / Contexte</span><span>Date</span><span>Statut</span><span></span>
        </div>
        {loading && <p className="text-sm text-marine/80 p-5">Chargement…</p>}
        {!loading && filtered.length === 0 && (
          <p className="text-sm text-marine/80 p-5" data-testid="email-log-empty">Aucun e-mail ne correspond à ce filtre.</p>
        )}
        {!loading && filtered.map((l) => (
          <div key={l.id} data-testid={`email-log-${l.id}`} className="grid md:grid-cols-[1.8fr_2.3fr_1.3fr_0.9fr_108px] gap-4 px-5 py-3 border-b border-marine/5 items-center text-sm bg-creme hover:bg-sable">
            <span className="text-marine truncate flex items-center gap-1.5"><Icons.Mail size={16} className="text-bordeaux shrink-0" /> {l.to_email}</span>
            <span className="text-marine/80 truncate">{l.subject}{l.context_label ? ` · ${l.context_label}` : ""}</span>
            <span className="text-marine/80">{fmtDateTime(l.created_at)}</span>
            <span>
              <span className={`text-[9px] uppercase tracking-wider px-2 py-1 rounded-sm ${STATUS_STYLE[l.status] || "bg-sable-clair text-marine/80"}`}>{l.status}</span>
              {l.error ? <span className="block text-xs text-rouge mt-0.5 truncate" title={l.error}>{l.error}</span> : null}
            </span>
            <button onClick={() => resend(l)} disabled={resending === l.id} data-testid={`email-log-resend-${l.id}`}
              title="Renvoyer cet e-mail"
              className="justify-self-end inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm border border-marine/20 text-marine hover:bg-marine hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              {resending === l.id ? <Icons.Loader2 size={16} className="animate-spin" /> : <Icons.Send size={16} />}
              Renvoyer
            </button>
          </div>
        ))}
      </div>
      <p className="text-xs text-marine/80 mt-3">Les 200 derniers envois sont affichés. « Simulé » = envoi Resend non configuré (mode démonstration). Le renvoi groupé nécessite de sélectionner une période (jour, mois ou année).</p>

      {bulk && (
        <Modal onClose={() => !bulkBusy && setBulk(null)} title={bulk.mode === "failed" ? "Renvoyer les e-mails en échec" : "Renvoi groupé des e-mails"}>
          <div className="space-y-4" data-testid="bulk-resend-modal">
            <p className="text-sm text-marine">
              Vous êtes sur le point de renvoyer <strong>{bulkTargets.length} e-mail(s)</strong>
              {bulk.mode === "failed"
                ? <> en échec pour <strong>{periodLabel}</strong>.</>
                : <> correspondant à la sélection actuelle{status !== "all" ? ` (statut « ${status} »)` : ""} pour <strong>{periodLabel}</strong>.</>}
            </p>
            <p className="text-xs text-marine/80">Chaque destinataire recevra à nouveau l'e-mail correspondant. Cette action est immédiate.</p>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setBulk(null)} disabled={bulkBusy} data-testid="bulk-cancel"
                className="px-4 py-2 text-sm rounded-sm border border-marine/20 text-marine hover:bg-creme transition-colors">Annuler</button>
              <button onClick={doBulk} disabled={bulkBusy || bulkTargets.length === 0} data-testid="bulk-confirm"
                className="px-4 py-2 text-sm rounded-sm bg-bordeaux text-white hover:bg-bordeaux-hover transition-colors inline-flex items-center gap-2 disabled:opacity-50">
                {bulkBusy ? <Icons.Loader2 size={16} className="animate-spin" /> : <Icons.SendHorizontal size={16} />}
                Confirmer le renvoi
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
